import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { siftApiClient } from '../../api/siftApiClient';
import type {
  SalesViewClient,
  SalesViewSummary,
  SalesFilterOptions,
  SalesViewMapPoint,
  PlannedStop,
  SalesScoreCatalog,
  SalesCloseOutStatus,
  SalesSortMode,
  SalesTargetList,
} from '../../api/types';
import { formatIngestAge, formatIngestTs } from '../shared/dataFreshness';
import {
  EMPTY_FILTERS,
  applyRoleDefaults,
  hasActiveFilters,
  toParams,
  type SalesFilterState,
  type SalesFlagFilters,
} from './salesViewFilters';
import { SalesEntryFilters } from './SalesEntryFilters';
import { SalesChipFilters } from './SalesChipFilters';
import { SalesClientSearch } from './SalesClientSearch';
import { SalesClientCard } from './SalesClientCard';
import { SalesCarouselControls } from './SalesCarouselControls';
import { SalesTargetLists } from './SalesTargetLists';
import { SalesViewMap } from './SalesViewMap';
import { SalesClientDrawer } from './SalesClientDrawer';
import { SalesAgentWelcome } from './SalesAgentWelcome';
import { SalesPlanBoard } from './SalesPlanBoard';
import { SalesEmailNudge } from './SalesEmailNudge';
import { AgentChatWidget } from '../agent/AgentChatWidget';
import type { AgentHandoff } from '../agent/AgentChatWidget';
import type { AgentAction, AgentScope } from '../../api/siftApiClient';
import { useMediaQuery, COMPACT_QUERY, PHONE_QUERY } from '../../hooks/useMediaQuery';
import { LS_SHOW_MAP, countSheetFilters, initialShowMap } from './salesViewLayout';
import './SalesViewDashboard.css';

/** Geometry persists per §6.1, namespaced so it can never collide with the
 *  Client View keys this layout was forked from. */
const LS_CAROUSEL_WIDTH = 'sift:sales-view:carouselWidth';
const LS_PAGE_SIZE = 'sift:sales-view:pageSize';


const MIN_CAROUSEL = 320;
const MIN_MAP = 400;
const DEFAULT_CAROUSEL = 400;
const PAGE_SIZES = [10, 25, 50, 100];

const NO_FLAG_FILTERS: SalesFlagFilters = {
  wentDark: false,
  targetMissing: false,
};

interface SalesViewDashboardProps {
  userRole: string;
  impersonatedRole: string | null;
  /** When false, Mia is hidden (gated by the sales_mia_agent feature flag). */
  agentEnabled?: boolean;
}

export function SalesViewDashboard({ impersonatedRole, agentEnabled = false }: SalesViewDashboardProps) {
  const [options, setOptions] = useState<SalesFilterOptions | null>(null);
  const [filters, setFilters] = useState<SalesFilterState>(EMPTY_FILTERS);
  const [flags, setFlags] = useState<SalesFlagFilters>(NO_FLAG_FILTERS);

  const [clients, setClients] = useState<SalesViewClient[]>([]);
  const [totalClients, setTotalClients] = useState(0);
  const [summary, setSummary] = useState<SalesViewSummary | null>(null);
  const [scoreVersion, setScoreVersion] = useState<string | null>(null);
  const [dataAsOf, setDataAsOf] = useState<string | null>(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null);
  const [drawerClientId, setDrawerClientId] = useState<string | null>(null);
  const [miaHandoff, setMiaHandoff] = useState<AgentHandoff | null>(null);
  /** Map hover -> card highlight (§6.4). Only highlights, never scrolls: the
   *  hovered pin is often on a different page than the carousel is showing. */
  const [hoveredClientId, setHoveredClientId] = useState<string | null>(null);

  const [mapPoints, setMapPoints] = useState<SalesViewMapPoint[]>([]);
  const [ungeocodedCount, setUngeocodedCount] = useState(0);
  const [mapLoading, setMapLoading] = useState(true);

  /** The plan's VISIT stops, so the map can draw the route beside the list. */
  const [planStops, setPlanStops] = useState<PlannedStop[]>([]);
  /* Clients added from the BOARD, where SalesPlanView is not mounted and so cannot report
   * back -- without this a card stayed "not in plan" until something remounted the plan.
   * Deliberately NOT solved by fetching the plan here: /plan/current freezes the week on
   * first read, so merely opening a board would create that rep's plan of record. */
  const [pendingPlanIds, setPendingPlanIds] = useState<ReadonlySet<string>>(new Set());
  const planClientIds = useMemo(
    () => new Set([...planStops.map((s) => s.client_id), ...pendingPlanIds]),
    [planStops, pendingPlanIds],
  );

  const handlePlanStopsChange = useCallback((stops: PlannedStop[]) => {
    setPlanStops(stops);
    // The server has now said what is really in the plan, so the guesses can go.
    setPendingPlanIds((prev) => (prev.size ? new Set<string>() : prev));
  }, []);

  /* Board or plan. The plan used to be a right-hand slide-over, which covered the map --
   * and the map is where the route is. There is no second surface now: SalesPlanView owns
   * the whole feature and the dashboard only says which mode is showing. */
  const [viewMode, setViewMode] = useState<'board' | 'plan'>('board');
  /* Bumped when a client is added from the board, so the plan reloads without the
   * dashboard having to own plan state again. */
  const [planReloadToken, setPlanReloadToken] = useState(0);
  /** CSE-17: bumped whenever a target list changes, so Target pills and target-filtered pages refetch. */
  const [targetsVersion, setTargetsVersion] = useState(0);
  const [canEditTargets, setCanEditTargets] = useState(false);
  const [targetLists, setTargetLists] = useState<SalesTargetList[]>([]);
  const bumpTargets = useCallback(() => setTargetsVersion((n) => n + 1), []);
  const untargetClient = useCallback(async (clientId: string) => {
    try {
      await siftApiClient.untargetSalesClients(clientId);
      bumpTargets();
    } catch (err) {
      console.error('Untarget failed:', err);
    }
  }, [bumpTargets]);

  /* Clients with an add in flight. The ref is the guard -- it is current on the very next
   * click, before React has re-rendered -- and the state is what the card shows. */
  const addingRef = useRef<Set<string>>(new Set());
  const [addingIds, setAddingIds] = useState<ReadonlySet<string>>(new Set());

  const addToPlan = useCallback(async (clientId: string, mode: 'VISIT' | 'CALL') => {
    if (addingRef.current.has(clientId)) return;
    addingRef.current.add(clientId);
    setAddingIds(new Set(addingRef.current));
    try {
      await siftApiClient.addSalesPlanEdit(
        { action: 'added', client_id: clientId, source: 'list_edit', contact_mode: mode },
        { rep: filters.cse_territory_owner[0], impersonate_role: impersonatedRole ?? undefined },
      );
      // Only after the write succeeded: marking it first would show a stop the plan has not got.
      setPendingPlanIds((prev) => new Set(prev).add(clientId));
      setPlanReloadToken((n) => n + 1);
    } catch (err) {
      console.error('Could not add to plan:', err);
    } finally {
      addingRef.current.delete(clientId);
      setAddingIds(new Set(addingRef.current));
    }
  }, [filters.cse_territory_owner, impersonatedRole]);

  /* Mia can only PROPOSE a plan change; this is the click that carries it out. It is written
   * with source 'agent' and the conversation id, which is the only way to answer "did anyone
   * actually plan their week with Mia" -- the transcript alone cannot, because a rep can be
   * talked through a change and then make it by hand.
   *
   * Errors are thrown, not swallowed: the widget shows the failure next to the offer, and a
   * proposal that silently did nothing is worse than one that says it failed. */
  const confirmPlanProposal = useCallback(
    async (action: AgentAction, conversationId: string) => {
      if (!action.client_id || !action.action) return;
      await siftApiClient.addSalesPlanEdit(
        {
          action: action.action,
          client_id: action.client_id,
          source: 'agent',
          contact_mode: action.contact_mode,
          to_trip: action.to_trip,
          to_stop: action.to_stop,
          conversation_id: conversationId,
        },
        { rep: filters.cse_territory_owner[0], impersonate_role: impersonatedRole ?? undefined },
      );
      if (action.action === 'added' && action.client_id) {
        setPendingPlanIds((prev) => new Set(prev).add(action.client_id as string));
      }
      setPlanReloadToken((n) => n + 1);
    },
    [filters.cse_territory_owner, impersonatedRole],
  );

  /* Deep link from the Monday plan email (X3.4): ?plan=1 lands the rep on the
   * plan rather than on the board with an instruction to go find it. Read once
   * and stripped, so a refresh or a shared URL does not reopen it forever. */
  useEffect(() => {
    const url = new URL(window.location.href);
    if (url.searchParams.get('plan') !== '1') return;
    setViewMode('plan');
    url.searchParams.delete('plan');
    window.history.replaceState({}, '', url.toString());
  }, []);

  /* Signal catalogue: book-independent and version-stamped, so it is fetched once
   * here rather than per popout. Failure is non-fatal -- the breakdown falls back
   * to firing-only rows. */
  const [scoreCatalog, setScoreCatalog] = useState<SalesScoreCatalog | null>(null);
  useEffect(() => {
    const controller = new AbortController();
    (async () => {
      try {
        setScoreCatalog(await siftApiClient.getSalesViewScoreSignals(controller.signal));
      } catch (err) {
        if (!controller.signal.aborted) {
          console.error('Failed to load Sales View score signals:', err);
        }
      }
    })();
    return () => controller.abort();
  }, []);

  const [page, setPage] = useState(1);
  // Health stays the default so the board behaves exactly as before until a rep
  // opts into another axis (§7.3).
  const [sort, setSort] = useState<SalesSortMode>('health');
  const [bandCounts, setBandCounts] = useState<Record<string, number>>({});
  const [pageSize, setPageSize] = useState<number>(() => {
    const saved = Number(localStorage.getItem(LS_PAGE_SIZE));
    return PAGE_SIZES.includes(saved) ? saved : 25;
  });

  // Defaults must land BEFORE the first client fetch, or a rep watches the whole
  // 40k book flash past before their own territory replaces it.
  const [defaultsApplied, setDefaultsApplied] = useState(false);
  const roleDefaultsRef = useRef<SalesFilterState>(EMPTY_FILTERS);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const opts = await siftApiClient.getSalesViewFilterOptions(impersonatedRole || undefined);
        if (cancelled) return;
        setOptions(opts);
        const defaults = applyRoleDefaults(opts.defaults);
        roleDefaultsRef.current = defaults;
        setFilters(defaults);
      } catch (err) {
        if (!cancelled) console.error('Failed to load Sales View filter options:', err);
      } finally {
        if (!cancelled) setDefaultsApplied(true);
      }
    })();
    return () => { cancelled = true; };
  }, [impersonatedRole]);

  const params = useMemo(
    () => toParams(filters, flags, impersonatedRole),
    [filters, flags, impersonatedRole],
  );

  // A narrower filter can leave the current page past the end of the result set.
  useEffect(() => { setPage(1); }, [params, pageSize]);

  useEffect(() => {
    localStorage.setItem(LS_PAGE_SIZE, String(pageSize));
  }, [pageSize]);

  /*
   * Paged SERVER-side rather than fetched whole and sliced locally, which is what
   * Client View does. Client View can hold everything because its map-point rows
   * are thin; a Sales View row carries its reasons, headline metrics, aged rollup
   * and engagement block, so one page is the right payload and the endpoint
   * already takes limit/offset.
   */
  useEffect(() => {
    if (!defaultsApplied) return;
    const controller = new AbortController();
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const list = await siftApiClient.getSalesViewClients(
          {
            ...params,
            limit: pageSize,
            offset: (page - 1) * pageSize,
            sort,
          },
          controller.signal,
        );
        setClients(list.clients);
        setTotalClients(list.total_clients);
        setBandCounts(list.band_counts ?? {});
        setScoreVersion(list.score_version ?? null);
        setDataAsOf(list.data_as_of ?? null);
      } catch (err) {
        if (controller.signal.aborted) return;
        console.error('Failed to load Sales View clients:', err);
        setError('Could not load clients. Try again, or reset filters.');
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    })();
    return () => controller.abort();
  }, [defaultsApplied, params, page, pageSize, sort, targetsVersion]);

  // Filter-scoped but NOT page-scoped, so it is fetched separately -- paging must
  // not re-request the chip counts.
  useEffect(() => {
    if (!defaultsApplied) return;
    const controller = new AbortController();
    (async () => {
      try {
        setSummary(await siftApiClient.getSalesViewSummary(params, controller.signal));
      } catch {
        if (!controller.signal.aborted) setSummary(null);
      }
    })();
    return () => controller.abort();
  }, [defaultsApplied, params, targetsVersion]);

  const resetFilters = useCallback(() => {
    setFilters(roleDefaultsRef.current);
    setFlags(NO_FLAG_FILTERS);
  }, []);

  /*
   * Map points are filter-scoped but NOT page-scoped: the map shows the whole
   * filtered book while the carousel shows one page of it. Paging must never
   * redraw the map, so this deliberately does not depend on `page`.
   */
  useEffect(() => {
    if (!defaultsApplied) return;
    const controller = new AbortController();
    (async () => {
      setMapLoading(true);
      try {
        const res = await siftApiClient.getSalesViewMapPoints(params, controller.signal);
        setMapPoints(res.points);
        setUngeocodedCount(res.ungeocoded_count);
      } catch (err) {
        if (!controller.signal.aborted) {
          console.error('Failed to load Sales View map points:', err);
          setMapPoints([]);
        }
      } finally {
        if (!controller.signal.aborted) setMapLoading(false);
      }
    })();
    return () => controller.abort();
  }, [defaultsApplied, params, targetsVersion]);

  /* A dismissed client leaves the list for the rest of the cycle (§7), so drop it
   * locally rather than refetching the page -- and drop it from the map too, or
   * it stays as a pin the carousel can no longer explain. */
  const handleStatusChange = useCallback(
    (clientId: string, status: SalesCloseOutStatus) => {
      if (status === 'dismissed') {
        setClients((prev) => prev.filter((c) => c.client_id !== clientId));
        setMapPoints((prev) => prev.filter((p) => p.client_id !== clientId));
        setTotalClients((prev) => Math.max(0, prev - 1));
        setDrawerClientId(null);
        setSelectedClientId(null);
        return;
      }
      setClients((prev) =>
        prev.map((c) => (c.client_id === clientId ? { ...c, user_status: status } : c)),
      );
    },
    [],
  );

  const openClient = useCallback((clientId: string | null) => {
    setSelectedClientId(clientId);
    setDrawerClientId(clientId);
  }, []);

  // ----- Mia -----
  // The board scope Mia is given each turn. Without it she answers over the rep's whole territory
  // while the rep is looking at a filtered subset, and the two numbers disagree on screen.
  const agentScope = useMemo<AgentScope>(
    () => ({
      ...filters,
      went_dark: flags.wentDark || undefined,
      target_missing: flags.targetMissing || undefined,
    }),
    [filters, flags],
  );

  const agentSelection = useMemo(
    () => ({ client_id: drawerClientId ?? selectedClientId ?? undefined }),
    [drawerClientId, selectedClientId],
  );

  const handleAgentActions = useCallback(
    (actions: AgentAction[]) => {
      for (const action of actions) {
        switch (action.type) {
          case 'navigate_client':
          case 'highlight': {
            const id = action.client_id ?? action.target;
            if (id) openClient(id);
            break;
          }
          case 'apply_filter':
            // Chips UNION with what is already on the board rather than replacing it, matching
            // what clicking the chip yourself does.
            setPage(1);
            setFilters((prev) => {
              const next = { ...prev };
              for (const key of ['tier', 'reason_group', 'band'] as const) {
                const value = action[key];
                if (value && !next[key].includes(value)) next[key] = [...next[key], value];
              }
              return next;
            });
            break;
          case 'set_sort':
            if (action.sort) {
              setSort(action.sort);
              setPage(1);
            }
            break;
          case 'back':
            setDrawerClientId(null);
            break;
          case 'reset':
            setSelectedClientId(null);
            setDrawerClientId(null);
            break;
          default:
            // navigate_dealer / navigate_vin / set_view are Client View directives. The server
            // allowlist is shared, so ignore rather than trust that they cannot arrive here.
            break;
        }
      }
    },
    [openClient],
  );

  const selectedClientName = useMemo(
    () => clients.find((c) => c.client_id === (drawerClientId ?? selectedClientId))?.client_name ?? undefined,
    [clients, drawerClientId, selectedClientId],
  );


  const filtersActive = hasActiveFilters(filters, flags);
  const totalPages = Math.max(1, Math.ceil(totalClients / pageSize));
  const firstRow = totalClients === 0 ? 0 : (page - 1) * pageSize + 1;
  const lastRow = Math.min(page * pageSize, totalClients);

  // ----- Responsive layout -----
  const compact = useMediaQuery(COMPACT_QUERY);
  const phone = useMediaQuery(PHONE_QUERY);
  const [showMap, setShowMap] = useState<boolean>(() =>
    initialShowMap(localStorage.getItem(LS_SHOW_MAP), window.matchMedia(COMPACT_QUERY).matches),
  );
  const toggleMap = () => {
    const next = !showMap;
    setShowMap(next);
    // Saved only once chosen, so an untouched device keeps the default for its size.
    localStorage.setItem(LS_SHOW_MAP, next ? '1' : '0');
  };
  const [filtersOpen, setFiltersOpen] = useState(false);
  const sheetOpen = phone && filtersOpen;
  useEffect(() => {
    if (!sheetOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setFiltersOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [sheetOpen]);

  // ----- Carousel <-> map splitter -----
  const [carouselWidth, setCarouselWidth] = useState<number>(() => {
    const saved = Number(localStorage.getItem(LS_CAROUSEL_WIDTH));
    return Number.isFinite(saved) && saved >= MIN_CAROUSEL ? saved : DEFAULT_CAROUSEL;
  });
  const dragStateRef = useRef<{ startX: number; startW: number } | null>(null);

  // Pointer events with capture, so a finger on a touch laptop or a landscape tablet drags it too.
  const onSplitterPointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    const handle = e.currentTarget;
    handle.setPointerCapture?.(e.pointerId);
    dragStateRef.current = { startX: e.clientX, startW: carouselWidth };
    const onMove = (ev: PointerEvent) => {
      const s = dragStateRef.current;
      if (!s) return;
      const max = Math.max(MIN_CAROUSEL, window.innerWidth - MIN_MAP);
      setCarouselWidth(Math.min(max, Math.max(MIN_CAROUSEL, s.startW + (ev.clientX - s.startX))));
    };
    const onUp = () => {
      dragStateRef.current = null;
      handle.removeEventListener('pointermove', onMove);
      handle.removeEventListener('pointerup', onUp);
      handle.removeEventListener('pointercancel', onUp);
      // Leaflet only recomputes its size on a resize event.
      window.dispatchEvent(new Event('resize'));
    };
    handle.addEventListener('pointermove', onMove);
    handle.addEventListener('pointerup', onUp);
    handle.addEventListener('pointercancel', onUp);
  }, [carouselWidth]);

  useEffect(() => {
    localStorage.setItem(LS_CAROUSEL_WIDTH, String(carouselWidth));
  }, [carouselWidth]);

  const sheetFilterCount = countSheetFilters(filters);
  const drawer = drawerClientId ? (
    <SalesClientDrawer
      clientId={drawerClientId}
      impersonatedRole={impersonatedRole}
      scoreCatalog={scoreCatalog}
      opportunityCatalog={options?.opportunities}
      onClose={() => setDrawerClientId(null)}
      onStatusChange={handleStatusChange}
      fullScreen={compact}
      cardOnScreen={!compact && viewMode === 'board'}
      plannedStop={planStops.length
        ? planStops.find((s) => s.client_id === drawerClientId) ?? null
        : undefined}
      onContinueInMia={agentEnabled ? setMiaHandoff : undefined}
    />
  ) : null;
  const map = (
    <div className="sv-map-container">
      <SalesViewMap
        points={mapPoints}
        loading={mapLoading}
        ungeocodedCount={ungeocodedCount}
        selectedClientId={selectedClientId}
        onClientClick={openClient}
        onClientHover={setHoveredClientId}
        planStops={viewMode === 'plan' ? planStops : undefined}
      />
    </div>
  );
  // Desktop keeps the side-by-side layout; the popup takes the map's slot, or opens one if the map is off.
  const sidePane = compact ? null : (drawer ?? (showMap ? map : null));

  return (
    <div className={`sv-dashboard${compact ? ' sv-dashboard--compact' : ''}${phone ? ' sv-dashboard--phone' : ''}`}>
      {phone ? (
        <div className="sv-mobile-bar">
          <SalesClientSearch
            selected={filters.client_token}
            onChange={(next) => setFilters({ ...filters, client_token: next })}
          />
          <button
            type="button"
            className={`sv-filters-btn${sheetFilterCount > 0 ? ' active' : ''}`}
            aria-expanded={sheetOpen}
            onClick={() => setFiltersOpen(true)}
          >
            Filters
            {sheetFilterCount > 0 && <span className="sv-filterdd-badge">{sheetFilterCount}</span>}
          </button>
        </div>
      ) : (
      <div className="sv-filters-wrap">
        <SalesEntryFilters
          options={options}
          filters={filters}
          onFiltersChange={setFilters}
        />
        <div className="sv-freshness">
          {dataAsOf && (
            <span title={`Data as of ${formatIngestTs(dataAsOf)}`}>
              Data {formatIngestAge(dataAsOf)} old
            </span>
          )}
          {scoreVersion && <span title="Health score version">Score {scoreVersion}</span>}
        </div>
      </div>
      )}

      <div className="sv-control-row">
        {!phone && (
          <SalesChipFilters
            filters={filters}
            summary={summary}
            bandCounts={bandCounts}
            options={options}
            onFiltersChange={setFilters}
            variant={compact ? 'dropdowns' : 'rows'}
            targetLists={targetLists}
          />
        )}
        {filtersActive && !phone && (
          <button className="sv-reset-btn" onClick={resetFilters}>Reset Filters</button>
        )}
        <div className="sv-view-actions">
        <SalesTargetLists
          selected={filters.target_list}
          onChange={(ids) => setFilters((f) => ({ ...f, target_list: ids }))}
          impersonateRole={impersonatedRole ?? undefined}
          onChanged={bumpTargets}
          onEditable={setCanEditTargets}
          onLists={setTargetLists}
          reloadToken={targetsVersion}
        />
        <button
          type="button"
          className={`sv-map-toggle${showMap ? ' active' : ''}`}
          aria-pressed={showMap}
          onClick={toggleMap}
          title={showMap ? 'Hide the map and give the clients the full page' : 'Show the map'}
        >
          Map
        </button>
        <div className="sv-mode-switch" role="tablist" aria-label="Sales View mode">
          <button
            role="tab"
            aria-selected={viewMode === 'board'}
            className={`sv-mode-btn ${viewMode === 'board' ? 'active' : ''}`}
            onClick={() => setViewMode('board')}
          >
            Board
          </button>
          <button
            role="tab"
            aria-selected={viewMode === 'plan'}
            className={`sv-mode-btn ${viewMode === 'plan' ? 'active' : ''}`}
            onClick={() => setViewMode('plan')}
          >
            Plan My Week
            {planStops.length ? (
              <span className="sv-mode-count">{planStops.length}</span>
            ) : null}
          </button>
        </div>
        </div>
      </div>

      <div className={`sv-content${compact ? ' sv-content--stacked' : ''}`}>
        {compact && showMap && map}
        <div
          className={`sv-carousel${compact || !sidePane ? ' sv-carousel--full' : ''}`}
          style={compact || !sidePane ? undefined : { width: carouselWidth }}
        >
          {viewMode === 'plan' ? (
            <SalesPlanBoard
              impersonateRole={impersonatedRole ?? undefined}
              reps={filters.cse_territory_owner}
              selectedClientId={selectedClientId}
              onSelectClient={openClient}
              onHoverClient={setHoveredClientId}
              onStopsChange={handlePlanStopsChange}
              onStatusChange={(id, status) => handleStatusChange(id, status as never)}
              repOptions={options?.cse_territory_owners}
              reloadToken={planReloadToken}
            />
          ) : (
          <>
          <SalesEmailNudge
            impersonateRole={impersonatedRole ?? undefined}
            checkToken={viewMode}
            onPickReps={() => setViewMode('plan')}
          />
          <SalesCarouselControls
            filters={filters}
            flags={flags}
            summary={summary}
            options={options}
            sort={sort}
            onFiltersChange={setFilters}
            onFlagsChange={setFlags}
            onSortChange={(s) => { setSort(s); setPage(1); }}
          />

          <div className="sv-carousel-list">
            {error && <div className="sv-error">{error}</div>}

            {!loading && !error && clients.length === 0 && (
              <div className="sv-empty">
                <p>No clients match current filters</p>
                {filtersActive && (
                  <button className="sv-reset-btn" onClick={resetFilters}>Reset Filters</button>
                )}
              </div>
            )}

            {clients.map((c) => (
              <SalesClientCard
                key={c.client_id}
                client={c}
                selected={selectedClientId === c.client_id}
                highlighted={hoveredClientId === c.client_id}
                onClick={() => openClient(c.client_id)}
                onStatusChange={handleStatusChange}
                onAddToPlan={addToPlan}
                inPlan={planClientIds.has(c.client_id)}
                addingToPlan={addingIds.has(c.client_id)}
                onUntarget={canEditTargets ? untargetClient : undefined}
              />
            ))}
          </div>

          <div className="sv-pagination">
            <button
              className="sv-page-btn"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1 || loading}
              aria-label="Previous page"
            >
              ‹
            </button>
            <span className="sv-page-indicator">
              <span className="sv-page-range">
                {loading ? 'Loading…' : (
                  <>
                    {firstRow.toLocaleString()}–{lastRow.toLocaleString()} of{' '}
                    {totalClients.toLocaleString()}
                  </>
                )}
              </span>
            </span>
            <button
              className="sv-page-btn"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages || loading}
              aria-label="Next page"
            >
              ›
            </button>
            <select
              className="sv-page-size"
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
              aria-label="Cards per page"
            >
              {PAGE_SIZES.map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </div>
          </>
          )}
        </div>

        {sidePane && (
          <>
            <div
              className="sv-splitter"
              role="separator"
              aria-orientation="vertical"
              onPointerDown={onSplitterPointerDown}
            />
            {sidePane}
          </>
        )}
      </div>

      {compact && drawer && <div className="sv-drawer-overlay">{drawer}</div>}

      {sheetOpen && (
        <div className="sv-filter-sheet" role="dialog" aria-modal="true" aria-label="Filters">
          <div className="sv-filter-sheet-head">
            <span className="sv-filter-sheet-title">Filters</span>
            <button type="button" className="sv-filter-sheet-done" onClick={() => setFiltersOpen(false)}>
              Done
            </button>
          </div>
          <div className="sv-filter-sheet-body">
            <SalesEntryFilters
              options={options}
              filters={filters}
              onFiltersChange={setFilters}
              showSearch={false}
              stacked
            />
            <SalesChipFilters
              filters={filters}
              summary={summary}
              bandCounts={bandCounts}
              options={options}
              onFiltersChange={setFilters}
              variant="stack"
              targetLists={targetLists}
            />
            {dataAsOf && (
              <div className="sv-freshness">
                <span>Data {formatIngestAge(dataAsOf)} old</span>
                {scoreVersion && <span>Score {scoreVersion}</span>}
              </div>
            )}
          </div>
          <div className="sv-filter-sheet-foot">
            {filtersActive && (
              <button type="button" className="sv-reset-btn" onClick={resetFilters}>Reset</button>
            )}
            <button type="button" className="sv-filter-sheet-apply" onClick={() => setFiltersOpen(false)}>
              {loading ? 'Show clients' : `Show ${totalClients.toLocaleString()} clients`}
            </button>
          </div>
        </div>
      )}

      {agentEnabled && (
        <AgentChatWidget
          agentKey="sales"
          selection={agentSelection}
          scope={agentScope}
          scopeReady={defaultsApplied}
          selectionName={selectedClientName ?? undefined}
          onActions={handleAgentActions}
          onConfirmProposal={confirmPlanProposal}
          handoff={miaHandoff}
          welcomeCard={(send, busy) => (
            <SalesAgentWelcome
              ready={defaultsApplied && !loading}
              summary={summary}
              totalClients={totalClients}
              bandCounts={bandCounts}
              selectedClientName={selectedClientName}
              busy={busy}
              onPrompt={send}
            />
          )}
        />
      )}
    </div>
  );
}

export default SalesViewDashboard;
